/**
 * SafetyHeroWidget — full-width hero row of three headline cards across:
 * Predictive Safety Score, Safety Maturity (SSEM), and Top Open Risks.
 * PSS and SSEM open an in-place detail modal on click; Top Open Risks opens
 * its records grid.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useDashboard } from '../context/DashboardContext';
import { useKpis, useDashboardData } from '../hooks/useDashboardData';
import KpiWidget from './KpiWidget';
import PssWidget from './PssWidget';
import DrilldownModal from './DrilldownModal';
import InfoModal from './InfoModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SSEM = 'safety.ssem.maturity';
const TOP_RISKS = 'safety.incidents.high_severity_open';

function PssDetail() {
  const { siteId } = useDashboard();
  const { data, loading, error } = useDashboardData((signal) => {
    const p = siteId ? `?site_id=${encodeURIComponent(siteId)}` : '';
    return axios.get(`${API}/dashboard/pss${p}`, { signal }).then((r) => r.data);
  }, [siteId], {});

  if (loading) return <div className="h-32 animate-pulse rounded bg-gray-100" />;
  if (error) return <div className="text-sm text-red-600">Failed to load.</div>;
  const contributors = data?.contributors || {};
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <span className="text-4xl font-semibold tabular-nums text-purple-700">{data?.score ?? '—'}</span>
        <span className="text-sm text-gray-400">/ 100</span>
        {data?.risk_level && <span className="ml-auto text-sm text-gray-600">{data.risk_level}</span>}
      </div>
      {data?.message && <p className="text-sm text-gray-500">{data.message}</p>}
      {Object.keys(contributors).length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Contributors</div>
          {Object.entries(contributors).map(([k, v]) => (
            <div key={k}>
              <div className="mb-0.5 flex justify-between text-xs text-gray-600">
                <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                <span>{Math.round(Number(v) || 0)}</span>
              </div>
              <div className="h-1.5 rounded bg-gray-100">
                <div className="h-1.5 rounded bg-purple-500" style={{ width: `${Math.min(Math.max(Number(v) || 0, 0), 100)}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SsemDetail() {
  const { data, loading, error } = useDashboardData(
    (signal) => axios.get(`${API}/ssem/dashboard`, { signal }).then((r) => r.data),
    [], {},
  );
  if (loading) return <div className="h-32 animate-pulse rounded bg-gray-100" />;
  if (error) return <div className="text-sm text-red-600">Failed to load.</div>;
  const level = data?.current_overall_level ?? data?.overall_level;
  const name = data?.current_overall_level_name || data?.overall_level_name;
  const dims = data?.dimension_breakdown || [];
  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold text-teal-700">Level {level ?? '—'}</span>
        {name && <span className="text-sm text-gray-400">{name}</span>}
      </div>
      {dims.length > 0 && (
        <div className="space-y-1.5 pt-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">Dimensions</div>
          {dims.map((d, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-gray-600">{d.dimension || d.name || d.label || `Dimension ${i + 1}`}</span>
              <span className="font-medium">
                {d.level != null ? `Level ${d.level}` : (d.score != null ? d.score : '—')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SafetyHeroWidget() {
  const { module, siteId, time, departmentId, dimensions, fyStartMonth, compare, refreshMs } = useDashboard();
  const { metrics: values, loading } = useKpis({
    module, metrics: [SSEM, TOP_RISKS], siteId, time, departmentId, dimensions, fyStartMonth, compare, refreshMs,
  });
  const [drilled, setDrilled] = useState(false);
  const [modal, setModal] = useState(null); // 'pss' | 'ssem' | null

  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <PssWidget onClick={() => setModal('pss')} />
        <KpiWidget
          metric={values[SSEM]}
          loading={loading && !values[SSEM]}
          accent="teal"
          onClick={() => setModal('ssem')}
        />
        <KpiWidget
          metric={values[TOP_RISKS]}
          loading={loading && !values[TOP_RISKS]}
          accent="red"
          trend={values[TOP_RISKS]?.trend ?? null}
          trendLabel="vs previous period"
          onClick={() => setDrilled(true)}
        />
      </div>

      <DrilldownModal open={drilled} metric={TOP_RISKS} title="Top Open Risks" onClose={() => setDrilled(false)} />
      <InfoModal open={modal === 'pss'} title="Predictive Safety Score" onClose={() => setModal(null)}>
        <PssDetail />
      </InfoModal>
      <InfoModal open={modal === 'ssem'} title="Safety Maturity (SSEM)" onClose={() => setModal(null)}>
        <SsemDetail />
      </InfoModal>
    </>
  );
}
